import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ensureCsrfToken } from "../lib/csrf";
import { useHelpContext } from "../help/HelpContext";
import { useAccess } from "../access/AccessContext";
import { PERMISSIONS } from "../access/capabilities";
import ActiveStatusFilter from "../components/shared/ActiveStatusFilter";


export default function RiskDependencies() {
  const access = useAccess();
  const canAddDependency = access.hasPermission(PERMISSIONS.riskDependency.add);
  const canChangeDependency = access.hasPermission(PERMISSIONS.riskDependency.change);
  const canAddType = access.hasPermission(PERMISSIONS.riskDependencyType.add);
  const canChangeType = access.hasPermission(PERMISSIONS.riskDependencyType.change);
  const [activeTab, setActiveTab] = useState("dependencies");
  const [showActiveDependencies, setShowActiveDependencies] = useState(true);
  const { setCurrentViewId } = useHelpContext();

  useEffect(() => {
    setCurrentViewId(activeTab === "dependencies" ? "catalogue" : "types");
    return () => setCurrentViewId(null);
  }, [activeTab, setCurrentViewId]);

  const [dependencyTypes, setDependencyTypes] = useState([]);
  const [dependencies, setDependencies] = useState([]);

  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingDependencyId, setEditingDependencyId] = useState(null);

  const [message, setMessage] = useState(null);


  const [typeForm, setTypeForm] = useState({
    name: "",
    active: true,
  });

  const [dependencyForm, setDependencyForm] = useState({
    name: "",
    dependency_type: "",
    business_owner: "",
    criticality: "Medium",
    description: "",
    is_active: true,
  });

  const showMessage = (type, text) => {
    setMessage({ type, text });

    setTimeout(() => {
      setMessage(null);
    }, 4000);
  };

  const loadDependencyTypes = async () => {
    const res = await axios.get(
      "/api/digital-risk/risk-dependency-types/"
    );

    setDependencyTypes(res.data);
  };

  const loadDependencies = useCallback(async (active) => {
    const res = await axios.get(
      `/api/digital-risk/risk-dependencies/?active=${active}`
    );

    setDependencies(res.data);
  }, []);

  useEffect(() => {
    loadDependencyTypes();
  }, []);

  useEffect(() => {
    loadDependencies(showActiveDependencies);
  }, [loadDependencies, showActiveDependencies]);

  const resetTypeForm = () => {
    setEditingTypeId(null);

    setTypeForm({
      name: "",
      active: true,
    });
  };

  const resetDependencyForm = () => {
    setEditingDependencyId(null);

    setDependencyForm({
      name: "",
      dependency_type: "",
      business_owner: "",
      criticality: "Medium",
      description: "",
      is_active: true,
    });
  };

  const saveDependencyType = async () => {
   
    try {
    
      if (!typeForm.name.trim()) {
        showMessage("error", "Dependency type name is required.");
        return;
      }

      const csrfToken = await ensureCsrfToken();

      if (editingTypeId) {
        await axios.put(
        `/api/digital-risk/risk-dependency-types/${editingTypeId}/update/`,
        typeForm,
        {
            withCredentials: true,
            headers: {
            "X-CSRFToken": csrfToken,
            },
        }
        );

        showMessage("success", "Dependency type updated successfully.");
      } else {
        await axios.post(
        "/api/digital-risk/risk-dependency-types/",
        typeForm,
        {
            withCredentials: true,
            headers: {
            "X-CSRFToken": csrfToken,
            },
        }
        );

        showMessage("success", "Dependency type created successfully.");
      }

      resetTypeForm();
      loadDependencyTypes();
    } catch (error) {
      console.error(error.response?.data || error);
      showMessage("error", "Unable to save dependency type.");
    }
  };

  const editDependencyType = (item) => {
    setEditingTypeId(item.id);

    setTypeForm({
      name: item.name || "",
      active: item.active,
    });
  };


  const saveDependency = async () => {
    try {
        if (!dependencyForm.name.trim()) {
        showMessage(
            "error",
            "Dependency name is required."
        );

        return;
        }

        if (!dependencyForm.dependency_type) {
        showMessage(
            "error",
            "Dependency type is required."
        );

        return;
        }

        const csrfToken = await ensureCsrfToken();

        const requestConfig = {
        withCredentials: true,
        headers: {
            "X-CSRFToken": csrfToken,
        },
        };

        if (editingDependencyId) {
        await axios.put(
            `/api/digital-risk/risk-dependencies/${editingDependencyId}/update/`,
            dependencyForm,
            requestConfig
        );

        showMessage(
            "success",
            "Risk dependency updated successfully."
        );
        } else {
        await axios.post(
            "/api/digital-risk/risk-dependencies/",
            dependencyForm,
            requestConfig
        );

        showMessage(
            "success",
            "Risk dependency created successfully."
        );
        }

        resetDependencyForm();
        loadDependencies(showActiveDependencies);
    } catch (error) {
        console.error(error.response?.data || error);

        showMessage(
        "error",
        "Unable to save risk dependency."
        );
    }
  };

  const editDependency = (item) => {
    setEditingDependencyId(item.id);

    setDependencyForm({
      name: item.name || "",
      dependency_type: item.dependency_type_ref || item.dependency_type || "",
      business_owner: item.business_owner || "",
      criticality: item.criticality || "Medium",
      description: item.description || "",
      is_active: item.is_active,
    });
  };

  return (
    <div className="p-6 space-y-6">
      
      <div >
        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
            Scenario Modeling
        </p>

        <h1 className="text-2xl font-semibold text-slate-900 mt-2">
          Risk Dependencies
        </h1>

        <p className="text-slate-500 mt-2 max-w-3xl mt-1">
           Manage platform dependencies used across FAIR scenarios and correlation analysis.
        </p>

      </div>

      {message && (
        <div
          className={`
            px-4
            py-3
            rounded-xl
            text-sm
            font-medium

            ${
              message.type === "success"
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-red-100 text-red-800 border border-red-200"
            }
          `}
        >
          {message.text}
        </div>
      )}

      <div className="bg-white border rounded-2xl p-2 flex gap-2 w-fit">
        <button
          onClick={() => setActiveTab("dependencies")}
          className={`
            px-5
            py-3
            rounded-xl
            text-sm
            font-medium

            ${
              activeTab === "dependencies"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }
          `}
        >
          Dependency Catalogue
        </button>

        <button
          onClick={() => setActiveTab("types")}
          className={`
            px-5
            py-3
            rounded-xl
            text-sm
            font-medium

            ${
              activeTab === "types"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }
          `}
        >
          Dependency Types
        </button>
      </div>

      {activeTab === "dependencies" && (
        <>
          {(editingDependencyId ? canChangeDependency : canAddDependency) && <div className="bg-white border rounded-2xl p-5">
            <h2 className="font-semibold mb-4">
              {editingDependencyId
                ? "Edit Risk Dependency"
                : "New Risk Dependency"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                value={dependencyForm.name}
                onChange={(e) =>
                  setDependencyForm({
                    ...dependencyForm,
                    name: e.target.value,
                  })
                }
                placeholder="Dependency name"
                className="border rounded-xl px-4 py-3"
              />

              <select
                value={dependencyForm.dependency_type}
                onChange={(e) =>
                  setDependencyForm({
                    ...dependencyForm,
                    dependency_type: e.target.value,
                  })
                }
                className="border rounded-xl px-4 py-3"
              >
                <option value="">
                  Select dependency type
                </option>

                {dependencyTypes
                  .filter((type) => type.active)
                  .map((type) => (
                    <option
                      key={type.id}
                      value={type.id}
                    >
                      {type.name}
                    </option>
                  ))}
              </select>

              <input
                value={dependencyForm.business_owner}
                onChange={(e) =>
                  setDependencyForm({
                    ...dependencyForm,
                    business_owner: e.target.value,
                  })
                }
                placeholder="Business owner"
                className="border rounded-xl px-4 py-3"
              />

              <select
                value={dependencyForm.criticality}
                onChange={(e) =>
                  setDependencyForm({
                    ...dependencyForm,
                    criticality: e.target.value,
                  })
                }
                className="border rounded-xl px-4 py-3"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>

              <textarea
                value={dependencyForm.description}
                onChange={(e) =>
                  setDependencyForm({
                    ...dependencyForm,
                    description: e.target.value,
                  })
                }
                placeholder="Description"
                className="border rounded-xl px-4 py-3 md:col-span-2"
                rows={4}
              />

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={dependencyForm.is_active}
                  onChange={(e) =>
                    setDependencyForm({
                      ...dependencyForm,
                      is_active: e.target.checked,
                    })
                  }
                />

                Active
              </label>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={saveDependency}
                className="bg-slate-900 text-white px-5 py-3 rounded-xl"
              >
                {editingDependencyId ? "Update" : "Create"}
              </button>

              {editingDependencyId && (
                <button
                  onClick={resetDependencyForm}
                  className="border px-5 py-3 rounded-xl"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>}

          <div className="bg-white border rounded-2xl p-5">
            <h2 className="font-semibold mb-4">
              Dependency Catalogue
            </h2>
            <ActiveStatusFilter
              value={showActiveDependencies}
              onChange={setShowActiveDependencies}
              label="Dependency status"
            />

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="text-left py-3">
                    Dependency
                  </th>

                  <th className="text-left py-3">
                    Type
                  </th>

                  <th className="text-left py-3">
                    Business Owner
                  </th>

                  <th className="text-left py-3">
                    Criticality
                  </th>

                  <th className="text-left py-3">
                    Status
                  </th>

                  <th className="text-left py-3">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {dependencies.filter((item) => item.is_active === showActiveDependencies).map((item) => (
                  <tr
                    key={item.id}
                    className="border-b"
                  >
                    <td className="py-3 font-medium">
                      {item.name}
                    </td>

                    <td className="py-3">
                      {item.dependency_type_name}
                    </td>

                    <td className="py-3">
                      {item.business_owner || "—"}
                    </td>

                    <td className="py-3">
                      {item.criticality}
                    </td>

                    <td className="py-3">
                      {item.is_active ? "Active" : "Inactive"}
                    </td>

                    <td className="py-3">
                      {canChangeDependency && <button
                        onClick={() => editDependency(item)}
                        className="text-slate-700 hover:text-black"
                      >
                        Edit
                      </button>}
                    </td>
                  </tr>
                ))}

                {dependencies.filter((item) => item.is_active === showActiveDependencies).length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="py-6 text-slate-400"
                    >
                      No risk dependencies created yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "types" && (
        <>
          {(editingTypeId ? canChangeType : canAddType) && <div className="bg-white border rounded-2xl p-5">
            <h2 className="font-semibold mb-4">
              {editingTypeId
                ? "Edit Dependency Type"
                : "New Dependency Type"}
            </h2>

            <div className="flex flex-col md:flex-row gap-4">
              <input
                value={typeForm.name}
                onChange={(e) =>
                  setTypeForm({
                    ...typeForm,
                    name: e.target.value,
                  })
                }
                placeholder="Dependency type name"
                className="border rounded-xl px-4 py-3 flex-1"
              />

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={typeForm.active}
                  onChange={(e) =>
                    setTypeForm({
                      ...typeForm,
                      active: e.target.checked,
                    })
                  }
                />

                Active
              </label>

              <button
                onClick={saveDependencyType}
                className="bg-slate-900 text-white px-5 py-3 rounded-xl"
              >
                {editingTypeId ? "Update" : "Create"}
              </button>

              {editingTypeId && (
                <button
                  onClick={resetTypeForm}
                  className="border px-5 py-3 rounded-xl"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>}

          <div className="bg-white border rounded-2xl p-5">
            <h2 className="font-semibold mb-4">
              Dependency Types
            </h2>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500">
                  <th className="text-left py-3">
                    Name
                  </th>

                  <th className="text-left py-3">
                    Status
                  </th>

                  <th className="text-left py-3">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {dependencyTypes.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b"
                  >
                    <td className="py-3 font-medium">
                      {item.name}
                    </td>

                    <td className="py-3">
                      {item.active ? "Active" : "Inactive"}
                    </td>

                    <td className="py-3">
                      {canChangeType && <button
                        onClick={() => editDependencyType(item)}
                        className="text-slate-700 hover:text-black"
                      >
                        Edit
                      </button>}
                    </td>
                  </tr>
                ))}

                {dependencyTypes.length === 0 && (
                  <tr>
                    <td
                      colSpan="3"
                      className="py-6 text-slate-400"
                    >
                      No dependency types created yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
